import { useMemo } from "react";
import { PROMPT_EDIT_MODEL_LABELS } from "domains/assets/constants/promptEdit";
import { useAssetsByIds } from "domains/assets/hooks/useAssetsByIds";
import { getModelThumbnail } from "domains/models/utils";
import {
  SKYBOX_STYLES,
  SkyboxStyleKey,
} from "domains/skyboxes/constants/Skybox";
import {
  AsideCard,
  AsideSection,
  AsideTitledData,
  AsideTitledText,
} from "domains/ui/components/Aside";
import {
  GetAssetsByAssetIdApiResponse,
  GetJobIdApiResponse,
  GetModelsByModelIdApiResponse,
} from "infra/api/generated/api";
import _ from "lodash";

import { Text } from "@chakra-ui/react";

import AsideSectionParametersCustom from "./SectionParametersCustom";
import { ImageGrid } from "./SectionReference";
import { MultipleRefImages } from "./SectionRefs";

export interface AsideSectionTopProps {
  model: GetModelsByModelIdApiResponse["model"] | undefined;
  asset: GetAssetsByAssetIdApiResponse["asset"] | undefined;
  job: GetJobIdApiResponse["job"] | undefined;
}

export default function AsideSectionTop({
  model,
  asset,
  job,
}: AsideSectionTopProps) {
  const jobInput = job?.metadata.input;

  const assetType = asset?.metadata.type;
  const isImageEnhance = assetType === "upscale";
  const isExpand = assetType === "reframe";
  const isPromptEditing = assetType === "image-prompt-editing";
  const isRemoveBackground = assetType === "background-removal";
  const isRestyle = assetType === "restyle";
  const isPixelate = assetType === "pixelization";
  const isVectorize = assetType === "vectorization";
  const isSkybox = assetType === "skybox-base-360";
  const isSkyboxEnhance = assetType === "upscale-skybox";
  const isTextureEnhance = assetType === "upscale-texture";
  const isTexture = assetType === "texture";
  const isModeMap = assetType === "detection";
  const isCustom = job?.jobType === "custom";

  const assetIds = useMemo(() => {
    if (
      isImageEnhance ||
      isExpand ||
      isRemoveBackground ||
      isPixelate ||
      isVectorize ||
      isSkyboxEnhance ||
      isTextureEnhance ||
      isPromptEditing ||
      isModeMap
    ) {
      return [asset?.metadata.parentId];
    }

    if (isRestyle) {
      return [
        asset?.metadata.parentId,
        ...(job?.metadata?.input?.styleImages ?? []),
      ];
    }

    return [];
  }, [
    asset?.metadata.parentId,
    isModeMap,
    isExpand,
    isImageEnhance,
    isPixelate,
    isPromptEditing,
    isRemoveBackground,
    isRestyle,
    isSkyboxEnhance,
    isTextureEnhance,
    isVectorize,
    job?.metadata?.input?.styleImages,
  ]);

  const { assetsByIds } = useAssetsByIds({ assetIds });

  const parentImage = useMemo(() => {
    return asset?.metadata.parentId
      ? assetsByIds[asset?.metadata.parentId]?.meta
      : undefined;
  }, [asset?.metadata.parentId, assetsByIds]);

  const styleImages = useMemo(() => {
    const ids = (job?.metadata?.input?.styleImages ?? []) as string[];
    return _.compact(ids.map((id) => assetsByIds[id]?.meta));
  }, [job?.metadata?.input?.styleImages, assetsByIds]);

  if (assetType?.startsWith("uploaded") || assetType === "canvas-export") {
    return null;
  }

  return (
    <AsideSection id="top">
      {!!model && (
        <AsideTitledData title="Model">
          <AsideCard
            data-testid="asset-zoom-model-card"
            title={model.name}
            avatarUrl={
              getModelThumbnail(model, {
                width: 64,
              }).url
            }
            internalLink={{
              pathname: "/models/[id]",
              query: {
                id: model.id,
              },
            }}
            target="_blank"
          />
        </AsideTitledData>
      )}

      {asset?.metadata.prompt !== undefined && (
        <AsideTitledText
          title={isPromptEditing ? "Instructions" : "Prompt"}
          value={asset?.metadata.prompt ? asset.metadata.prompt : "-"}
          copyable={!!asset?.metadata.prompt}
        />
      )}

      {(isImageEnhance ||
        isRemoveBackground ||
        isPixelate ||
        isVectorize ||
        isSkyboxEnhance ||
        isTextureEnhance ||
        isModeMap) && <SingleImage image={parentImage} label="Input Image" />}

      {isExpand && (
        <>
          <SingleImage image={parentImage} label="Input Image" />
          {asset?.metadata.overlapPercentage && (
            <AsideTitledText
              title="Overlap"
              value={`${Math.round(
                asset.metadata.overlapPercentage * 2 * 100
              )}%`}
            />
          )}
          {asset?.metadata.resizeOption && (
            <AsideTitledText
              title="Input scale"
              value={`${Math.round(asset.metadata.resizeOption * 100)}%`}
            />
          )}
        </>
      )}

      {isPromptEditing && (
        <>
          <AsideTitledText
            title="Model"
            value={
              jobInput?.modelId ?? jobInput?.model
                ? PROMPT_EDIT_MODEL_LABELS[
                    (jobInput.modelId ??
                      jobInput.model) as keyof typeof PROMPT_EDIT_MODEL_LABELS
                  ] ??
                  jobInput.modelId ??
                  jobInput.model
                : "-"
            }
          />
          <SingleImage image={parentImage} label="Input Image" />
        </>
      )}

      {isRestyle && (
        <>
          <SingleImage image={parentImage} label="Input Image" />
          <MultipleImages images={styleImages} label="Style" />
        </>
      )}

      {isSkybox && (
        <AsideTitledText
          title="Style"
          value={
            asset?.metadata.style &&
            SKYBOX_STYLES[asset?.metadata.style as SkyboxStyleKey]
              ? SKYBOX_STYLES[asset?.metadata.style as SkyboxStyleKey].label
              : "Standard"
          }
        />
      )}

      {isCustom && (
        <AsideSectionParametersCustom model={model} job={job} section="top" />
      )}

      {isTexture && (
        <>
          <AsideTitledText
            title="Raised"
            value={
              asset?.metadata.raised ? asset.metadata.raised.toString() : "-"
            }
          />
          <AsideTitledText
            title="Shiny"
            value={
              asset?.metadata.shiny ? asset.metadata.shiny.toString() : "-"
            }
          />
          <AsideTitledText
            title="Polished"
            value={
              asset?.metadata.polished
                ? asset.metadata.polished.toString()
                : "-"
            }
          />
          <AsideTitledText
            title="Angular"
            value={
              asset?.metadata.angular ? asset.metadata.angular.toString() : "-"
            }
          />
          <AsideTitledText
            title="Inverted"
            value={asset?.metadata.invert ? "Yes" : "No"}
          />
        </>
      )}
    </AsideSection>
  );
}

function SingleImage({
  image,
  label,
}: {
  image: GetAssetsByAssetIdApiResponse["asset"] | undefined;
  label: string;
}) {
  return (
    <AsideTitledData title={label}>
      {image ? (
        <ImageGrid image={image} label={label} />
      ) : (
        <Text color="textSecondary" size="body.md">
          -
        </Text>
      )}
    </AsideTitledData>
  );
}

function MultipleImages({
  images,
  label,
}: {
  images: GetAssetsByAssetIdApiResponse["asset"][];
  label: string;
}) {
  return (
    <AsideTitledData title={label}>
      {images.length > 0 ? (
        <MultipleRefImages assets={images} />
      ) : (
        <Text color="textSecondary" size="body.md">
          -
        </Text>
      )}
    </AsideTitledData>
  );
}
